import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Lock, Copy, Heart, UserPlus, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { copyText } from "../lib/clipboard";
import { api } from "../lib/api";

const AVATAR_MAP = {
  "pink-w":   "/assets/home/avatar-pink-w.png",
  "purple-w": "/assets/home/avatar-purple-w.png",
  "orange-w": "/assets/home/avatar-orange-w.png",
  "red-m":    "/assets/home/avatar-red-m.png",
  "blue-m":   "/assets/home/avatar-blue-m.png",
  "green-m":  "/assets/home/avatar-green-m.png",
};
const AVATAR_IDS = Object.keys(AVATAR_MAP);

// Floating hearts/sparkles scattered across the screen (purely decorative)
function FloatingHearts({ count = 12 }) {
  const items = useMemo(() => {
    return Array.from({ length: count }, (_, i) => ({
      id: i,
      left: ((i * 17 + 7) % 96) + 1,
      top: ((i * 23 + 11) % 90) + 2,
      size: 10 + (i % 4) * 4,
      delay: (i * 0.5) % 6,
      dur: 3.4 + (i % 5) * 0.5,
      isSparkle: i % 3 === 0,
      color: i % 2 === 0 ? "#FF2E9A" : "#FF8FC8",
    }));
  }, [count]);
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden>
      {items.map((p) => {
        const I = p.isSparkle ? Sparkles : Heart;
        return (
          <motion.div
            key={p.id}
            className="absolute"
            style={{ left: `${p.left}%`, top: `${p.top}%` }}
            animate={{
              opacity: [0, 0.7, 0.7, 0],
              y: [-2, -12, -2],
              scale: [0.7, 1.05, 0.7],
              rotate: [-10, 10, -10],
            }}
            transition={{ duration: p.dur, delay: p.delay, repeat: Infinity, ease: "easeInOut" }}
          >
            <I
              size={p.size}
              style={{
                color: p.color,
                fill: p.isSparkle ? "none" : p.color,
                filter: `drop-shadow(0 0 5px ${p.color})`,
              }}
              strokeWidth={2}
            />
          </motion.div>
        );
      })}
    </div>
  );
}

// Tiny dust particles that drift slowly upward across the screen
function DustParticles({ count = 22 }) {
  const items = useMemo(() => {
    return Array.from({ length: count }, (_, i) => ({
      id: i,
      left: ((i * 31 + 13) % 100),
      size: 1 + (i % 4) * 0.6,
      delay: (i * 0.4) % 10,
      dur: 12 + (i % 7) * 1.4,
      color: i % 3 === 0 ? "#FF2E9A" : i % 3 === 1 ? "#9B6BFF" : "#FFC2E0",
      opacity: 0.25 + (i % 5) * 0.08,
    }));
  }, [count]);
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden>
      {items.map((p) => (
        <span
          key={p.id}
          style={{
            position: "absolute",
            top: 0,
            left: `${p.left}%`,
            width: p.size,
            height: p.size,
            borderRadius: "50%",
            background: p.color,
            boxShadow: `0 0 ${p.size * 4}px ${p.color}`,
            opacity: p.opacity,
            animation: `vcDustDrift ${p.dur}s linear infinite`,
            animationDelay: `${p.delay}s`,
          }}
        />
      ))}
      <style>{`
        @keyframes vcDustDrift {
          0%   { transform: translate3d(0, 110vh, 0) scale(0.6); opacity: 0; }
          12%  { opacity: 0.8; }
          50%  { transform: translate3d(8px, 50vh, 0) scale(1); }
          88%  { opacity: 0.8; }
          100% { transform: translate3d(-8px, -10vh, 0) scale(0.6); opacity: 0; }
        }
      `}</style>
    </div>
  );
}

/**
 * Join Room (deep-link arrival) — mimics the reference design:
 *  - Two cinematic avatars connected by dotted line + neon heart
 *  - "{Host} invited you 💌" gradient title
 *  - "Join the room and start the chaos 👀" subtitle
 *  - Floating hearts ambient
 *  - Pink-glowing Your-Name input
 *  - Purple-glowing room-code pill with lock + copy icons (auto-filled, read-only)
 *  - Big pink "Join the Room →" CTA
 *  - Footer privacy + "No signup needed ✨"
 */
export default function PlayRemotelyJoinRoom({
  code,
  defaultName = "",
  onBack,
  onJoin,
  loading,
}) {
  const [name, setName] = useState(defaultName);
  const [hostInfo, setHostInfo] = useState(null); // {host_name, host_avatar_id}
  const [autoAvatar, setAutoAvatar] = useState(null);
  const [cycled, setCycled] = useState(false);

  // Cycle to the next available avatar (skip host's)
  const cycleAvatar = () => {
    if (!autoAvatar) return;
    const hostAv = hostInfo?.host_avatar_id || "pink-w";
    const pool = AVATAR_IDS.filter((id) => id !== hostAv);
    const idx = pool.indexOf(autoAvatar);
    const next = pool[(idx + 1) % pool.length];
    setAutoAvatar(next);
    localStorage.setItem("remote_avatar_id", next);
    setCycled(true);
  };

  // Fetch host info to personalize the title + show the right portrait
  useEffect(() => {
    let alive = true;
    if (!code) return;
    api.getRoom(code)
      .then((room) => {
        if (!alive) return;
        setHostInfo({
          host_name: room.host_name || "Someone",
          host_avatar_id: room.host_avatar_id || "pink-w",
        });
        // Auto-pick a guest avatar that isn't the host's
        const hostAv = room.host_avatar_id || "pink-w";
        const candidates = AVATAR_IDS.filter((id) => id !== hostAv);
        const pick = candidates[Math.floor(Math.random() * candidates.length)] || "blue-m";
        // Persist so the Room polling logic + waiting-room can use it
        localStorage.setItem("remote_avatar_id", pick);
        setAutoAvatar(pick);
      })
      .catch(() => {
        // Room may not exist yet or be private — leave defaults; join attempt will surface error
        if (!alive) return;
        const pick = "blue-m";
        localStorage.setItem("remote_avatar_id", pick);
        setAutoAvatar(pick);
      });
    return () => { alive = false; };
  }, [code]);

  const handleSubmit = (e) => {
    e?.preventDefault?.();
    if (!name.trim()) {
      toast.error("What should they call you?");
      return;
    }
    onJoin?.({ name: name.trim(), avatarId: autoAvatar || "blue-m" });
  };

  const copyCode = async () => {
    const ok = await copyText(code || "");
    if (ok) toast.success(`Room code ${code} copied`);
  };

  const hostName = hostInfo?.host_name || "Someone";
  const hostAvatar  = AVATAR_MAP[hostInfo?.host_avatar_id] || AVATAR_MAP["pink-w"];
  const guestAvatar = AVATAR_MAP[autoAvatar] || AVATAR_MAP["blue-m"];

  return (
    <div className="vc-bg min-h-screen relative overflow-hidden" data-testid="remotely-join-screen">
      <DustParticles count={26} />
      <FloatingHearts count={14} />

      <main className="relative z-[5] max-w-md mx-auto px-5 pt-5 pb-12 min-h-screen flex flex-col">
        {/* Top bar */}
        <div className="flex items-center justify-between mb-2 relative z-[2]">
          <button
            onClick={onBack}
            aria-label="Back"
            data-testid="remotely-join-back"
            className="w-10 h-10 rounded-full flex items-center justify-center text-white/85 hover:text-white transition active:scale-95"
            style={{ background: "rgba(255, 255, 255, 0.06)", border: "1px solid rgba(255, 255, 255, 0.10)", backdropFilter: "blur(8px)" }}
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="w-10" aria-hidden />
        </div>

        {/* Two avatars + connecting dotted line + central heart */}
        <div className="relative h-[210px] flex items-center justify-center mb-5 mt-4" aria-hidden>
          {/* Host avatar (left, pink ring) */}
          <motion.div
            animate={{ y: [0, -5, 0] }}
            transition={{ duration: 3.2, repeat: Infinity, ease: "easeInOut" }}
            className="absolute w-[108px] h-[108px] rounded-full overflow-hidden z-[3]"
            style={{
              left: "8%",
              top: "50%",
              transform: "translateY(-50%)",
              border: "3px solid #FF2E9A",
              boxShadow: "0 0 24px rgba(255, 46, 154, 0.85), 0 0 48px rgba(255, 46, 154, 0.35)",
            }}
          >
            <img src={hostAvatar} alt={hostName} className="w-full h-full object-cover" />
          </motion.div>

          {/* Dotted connection line — purple/pink, slowly flowing dots */}
          <svg
            className="absolute pointer-events-none"
            width="100%" height="140"
            viewBox="0 0 360 140"
            preserveAspectRatio="none"
            style={{ top: "50%", transform: "translateY(-50%)", filter: "drop-shadow(0 0 5px rgba(255, 46, 154, 0.75))" }}
            aria-hidden
          >
            {/* Static base line (subtle) */}
            <line
              x1="100" y1="70" x2="260" y2="70"
              stroke="rgba(255, 46, 154, 0.25)"
              strokeWidth="2"
              strokeDasharray="3 6"
            />
            {/* Animated flowing dots — host → heart */}
            <line
              x1="100" y1="70" x2="180" y2="70"
              stroke="#FF2E9A" strokeWidth="2.6"
              strokeDasharray="3 6" strokeLinecap="round"
              opacity="0.95"
            >
              <animate attributeName="stroke-dashoffset" from="9" to="0" dur="0.9s" repeatCount="indefinite" />
            </line>
            {/* Animated flowing dots — heart → guest (opposite direction so they meet at the heart) */}
            <line
              x1="180" y1="70" x2="260" y2="70"
              stroke="#9B6BFF" strokeWidth="2.6"
              strokeDasharray="3 6" strokeLinecap="round"
              opacity="0.95"
            >
              <animate attributeName="stroke-dashoffset" from="0" to="9" dur="0.9s" repeatCount="indefinite" />
            </line>
          </svg>

          {/* Central heart — layered radial glow that pulses */}
          <div className="relative z-[4] flex items-center justify-center">
            {/* Inner glow halo */}
            <motion.span
              className="absolute rounded-full pointer-events-none"
              style={{
                width: 90, height: 90,
                background: "radial-gradient(circle, rgba(255,46,154,0.55) 0%, rgba(255,46,154,0.18) 40%, transparent 75%)",
                filter: "blur(8px)",
              }}
              animate={{ scale: [1, 1.35, 1], opacity: [0.55, 0.95, 0.55] }}
              transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
              aria-hidden
            />
            {/* Mid glow ring */}
            <motion.span
              className="absolute rounded-full pointer-events-none"
              style={{
                width: 56, height: 56,
                background: "radial-gradient(circle, rgba(255,143,200,0.65) 0%, transparent 70%)",
                filter: "blur(4px)",
              }}
              animate={{ scale: [1, 1.2, 1], opacity: [0.65, 1, 0.65] }}
              transition={{ duration: 1.4, repeat: Infinity, ease: "easeInOut" }}
              aria-hidden
            />
            {/* The heart itself */}
            <motion.div
              animate={{ scale: [1, 1.18, 1] }}
              transition={{ duration: 1.4, repeat: Infinity, ease: "easeInOut" }}
              className="relative"
            >
              <Heart
                className="w-12 h-12"
                style={{
                  color: "#FF2E9A",
                  fill: "#FF2E9A",
                  filter: "drop-shadow(0 0 14px #FF2E9A) drop-shadow(0 0 28px rgba(255, 46, 154, 0.7))",
                }}
                strokeWidth={2}
              />
            </motion.div>
            {/* Tiny orbiting dust particles around the heart */}
            {[0, 1, 2, 3, 4, 5].map((i) => {
              const angle = (i / 6) * 360;
              const radius = 40;
              return (
                <motion.span
                  key={i}
                  className="absolute pointer-events-none rounded-full"
                  style={{
                    width: i % 2 === 0 ? 3 : 2,
                    height: i % 2 === 0 ? 3 : 2,
                    background: i % 2 === 0 ? "#FF8FC8" : "#C9B7FF",
                    boxShadow: `0 0 6px ${i % 2 === 0 ? "#FF8FC8" : "#C9B7FF"}`,
                    left: "50%", top: "50%",
                    marginLeft: -1.5, marginTop: -1.5,
                  }}
                  animate={{
                    x: [
                      Math.cos((angle * Math.PI) / 180) * radius,
                      Math.cos(((angle + 360) * Math.PI) / 180) * radius,
                    ],
                    y: [
                      Math.sin((angle * Math.PI) / 180) * radius,
                      Math.sin(((angle + 360) * Math.PI) / 180) * radius,
                    ],
                    opacity: [0.4, 1, 0.4],
                  }}
                  transition={{
                    duration: 6 + (i % 3),
                    repeat: Infinity,
                    ease: "linear",
                    delay: i * 0.3,
                  }}
                  aria-hidden
                />
              );
            })}
          </div>

          {/* Guest avatar (right, purple ring) — tappable to cycle through choices */}
          <motion.button
            type="button"
            onClick={cycleAvatar}
            data-testid="remotely-join-guest-avatar"
            aria-label="Change your look"
            disabled={!autoAvatar}
            animate={{ y: [0, -5, 0] }}
            transition={{ duration: 3.2, repeat: Infinity, ease: "easeInOut", delay: 1.6 }}
            whileTap={{ scale: 0.94 }}
            className="absolute w-[108px] h-[108px] rounded-full overflow-hidden z-[3] p-0"
            style={{
              right: "8%",
              top: "50%",
              transform: "translateY(-50%)",
              border: "3px solid #9B6BFF",
              boxShadow: "0 0 24px rgba(155, 107, 255, 0.85), 0 0 48px rgba(155, 107, 255, 0.35)",
              background: "rgba(15, 8, 22, 0.5)",
              cursor: autoAvatar ? "pointer" : "default",
            }}
          >
            <motion.div
              key={autoAvatar || "loading"}
              initial={{ rotateY: -90, opacity: 0 }}
              animate={{ rotateY: 0, opacity: 1 }}
              transition={{ type: "spring", stiffness: 280, damping: 22 }}
              className="w-full h-full"
              style={{ transformStyle: "preserve-3d", backfaceVisibility: "hidden" }}
            >
              {autoAvatar ? (
                <img src={guestAvatar} alt="You" className="w-full h-full object-cover" draggable={false} />
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-[#1a0e22]">
                  <span className="text-[32px]" style={{ filter: "drop-shadow(0 0 6px #9B6BFF)" }}>💜</span>
                </div>
              )}
            </motion.div>
          </motion.button>

          {/* "Tap to change" hint — small chip below guest avatar, fades after first interaction */}
          {autoAvatar && !cycled && (
            <motion.div
              initial={{ opacity: 0, y: -6 }}
              animate={{ opacity: [0, 1, 1, 0.85], y: 0 }}
              transition={{ duration: 1.6, delay: 0.6, repeat: Infinity, repeatDelay: 1.6, ease: "easeInOut" }}
              className="absolute pointer-events-none z-[5]"
              style={{
                right: "8%",
                bottom: 0,
                transform: "translateX(0)",
              }}
            >
              <div
                className="inline-flex items-center gap-1 px-2 py-[3px] rounded-full text-[9.5px] font-extrabold tracking-wide"
                style={{
                  background: "rgba(20, 14, 26, 0.85)",
                  border: "1px solid rgba(155, 107, 255, 0.6)",
                  color: "#C9B7FF",
                  boxShadow: "0 0 10px rgba(155, 107, 255, 0.5)",
                  whiteSpace: "nowrap",
                }}
              >
                tap to change ✨
              </div>
            </motion.div>
          )}
        </div>

        {/* Title */}
        <div className="text-center mb-1 relative z-[2]">
          <h1
            className="font-extrabold leading-[1.05] tracking-tight"
            style={{
              fontFamily: "'Manrope', sans-serif",
              fontSize: "clamp(1.9rem, 8vw, 2.3rem)",
              letterSpacing: "-0.025em",
              color: "#FFFFFF",
            }}
            data-testid="remotely-join-title"
          >
            <span
              style={{
                background: "linear-gradient(90deg, #FFD8E8 0%, #FF4FBF 55%, #FF0F7B 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
                textShadow: "0 0 1px rgba(255, 46, 154, 0.6)",
                filter: "drop-shadow(0 1px 1.5px rgba(0, 0, 0, 0.55))",
              }}
            >
              {hostName}
            </span>{" "}
            invited you{" "}
            <motion.span
              animate={{ scale: [1, 1.18, 1] }}
              transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
              className="inline-block"
              style={{ filter: "drop-shadow(0 0 8px #FF2E9A)" }}
            >
              💌
            </motion.span>
          </h1>
          <p className="text-white/75 text-[14px] font-medium mt-2 leading-snug">
            Join the room and start the chaos
            <motion.span
              animate={{ scale: [1, 1.18, 1] }}
              transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}
              className="inline-block ml-1"
            >
              👀
            </motion.span>
          </p>
        </div>

        <form onSubmit={handleSubmit} className="mt-6 space-y-5">
          {/* Name input */}
          <div>
            <label
              className="block text-[#FF2E9A] text-[11px] font-extrabold tracking-[0.22em] mb-2.5 px-1"
              style={{ filter: "drop-shadow(0 0 6px rgba(255, 46, 154, 0.45))" }}
            >
              WHAT SHOULD THEY CALL YOU?
            </label>
            <div className="relative">
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your name"
                maxLength={32}
                autoFocus
                data-testid="remotely-join-name-input"
                className="w-full rounded-2xl px-5 py-[16px] pr-12 text-white text-[16px] font-bold placeholder:text-white/35 focus:outline-none transition"
                style={{
                  background: "rgba(15, 8, 22, 0.6)",
                  border: "2px solid #FF2E9A",
                  boxShadow: "0 0 16px rgba(255, 46, 154, 0.5)",
                  fontFamily: "'Manrope', sans-serif",
                }}
              />
              <UserPlus
                className="absolute right-4 top-1/2 -translate-y-1/2 w-[18px] h-[18px]"
                style={{ color: "#FF2E9A", filter: "drop-shadow(0 0 5px #FF2E9A)" }}
                strokeWidth={2.2}
              />
            </div>
          </div>

          {/* Room code (auto-filled, read-only) */}
          <div>
            <label
              className="block text-[#9B6BFF] text-[11px] font-extrabold tracking-[0.22em] mb-2.5 px-1"
              style={{ filter: "drop-shadow(0 0 6px rgba(155, 107, 255, 0.5))" }}
            >
              ROOM CODE
            </label>
            <button
              type="button"
              onClick={copyCode}
              data-testid="remotely-join-code-pill"
              className="w-full rounded-full flex items-center justify-between px-5 py-3 transition active:scale-[0.99]"
              style={{
                background: "rgba(15, 8, 22, 0.55)",
                border: "2px solid #9B6BFF",
                boxShadow: "0 0 18px rgba(155, 107, 255, 0.55)",
              }}
              aria-label={`Room code ${code}, tap to copy`}
            >
              <Lock className="w-[18px] h-[18px]" style={{ color: "#9B6BFF", filter: "drop-shadow(0 0 5px #9B6BFF)" }} strokeWidth={2.2} />
              <span
                className="text-white font-extrabold flex-1 text-center"
                style={{
                  fontFamily: "'Manrope', sans-serif",
                  fontSize: "20px",
                  letterSpacing: "0.18em",
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                {code || "------"}
              </span>
              <Copy className="w-[18px] h-[18px]" style={{ color: "#9B6BFF", filter: "drop-shadow(0 0 5px #9B6BFF)" }} strokeWidth={2.2} />
            </button>
            <p className="text-center text-white/55 text-[12px] font-medium mt-2 inline-flex items-center justify-center w-full gap-1.5">
              Auto-filled from invite link
              <motion.span
                animate={{ rotate: [0, 12, -12, 0], scale: [1, 1.15, 1] }}
                transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
              >
                ✨
              </motion.span>
            </p>
          </div>

          {/* Big CTA */}
          <button
            type="submit"
            disabled={loading}
            data-testid="remotely-join-submit"
            className="vc-cta vc-cta-primary w-full rounded-full py-[20px] font-extrabold flex items-center justify-center gap-3 mt-2"
            style={{ fontSize: "19px", letterSpacing: "0.01em" }}
          >
            {loading ? "Joining…" : (
              <>
                Join the Room
                <ArrowRight className="w-6 h-6" strokeWidth={2.6} />
              </>
            )}
          </button>
        </form>

        <div className="flex-1 min-h-[16px]" />

        {/* Footer privacy + tagline */}
        <div className="flex flex-col items-center gap-3 mt-6">
          <div className="flex items-center justify-center gap-3 text-white/70 text-[12.5px] font-bold">
            <span className="inline-flex items-center gap-1.5">
              <Lock className="w-[13px] h-[13px]" />
              Private room
            </span>
            <span className="w-1 h-1 rounded-full bg-white/30" aria-hidden />
            <span className="inline-flex items-center gap-1.5">
              <Heart className="w-[13px] h-[13px]" style={{ color: "#C088FF" }} strokeWidth={2} />
              Just you two
              <span style={{ filter: "drop-shadow(0 0 5px #9B6BFF)" }}>💜</span>
            </span>
          </div>

          {/* Heart divider */}
          <div className="flex items-center justify-center w-full">
            <span className="w-12 h-px bg-white/15" />
            <Heart className="w-3 h-3 mx-2" style={{ color: "#FF2E9A", fill: "#FF2E9A", filter: "drop-shadow(0 0 4px #FF2E9A)" }} strokeWidth={2} />
            <span className="w-12 h-px bg-white/15" />
          </div>

          <div className="flex items-center justify-center gap-1.5 text-white/55 text-[12px] font-medium">
            <motion.span animate={{ scale: [1, 1.2, 1] }} transition={{ duration: 2, repeat: Infinity }}>✨</motion.span>
            No signup needed
            <motion.span animate={{ scale: [1, 1.2, 1] }} transition={{ duration: 2, repeat: Infinity, delay: 1 }}>✨</motion.span>
          </div>
        </div>
      </main>
    </div>
  );
}
